import {
  GridColumns,
  GridValueFormatterParams,
  GridRenderCellParams
} from '@mui/x-data-grid-premium';
import { filterOperators, numberFilterOperators } from '../../../../constants';
import { useTranslation } from 'react-i18next';
import { baseCell } from '../cells/base-cell';
import { currencyFormatter } from 'helpers/currency-formatter';
import { currencyCell } from '../cells/currency-cell';
import listCell from '../cells/list-cell';
import { ActionMenuCell } from '../cells/action-menu-cell';
import { isBottomFromTenantAdmin } from 'components/helpers/IsBottomFromTenantAdmin';

export function ThresholdsColumns(
  disableEdit: boolean,
  onManagePrices?: (product: string) => void,
  refresh?: any
): GridColumns {
  const { t } = useTranslation();

  const columns: GridColumns = [
    {
      field: 'product',
      headerName: t(`global.product`),
      filterOperators,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterOperators,
      width: 400,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`entities.product.status`),
      width: 100,
      renderCell: ({ value }) => {
        return (
          <span className={value === 'ACTIVE' ? 'text-green-500' : 'text-orange-500'}>
            {t(`entities.product.status_options.${value}`)}
          </span>
        );
      }
    },
    {
      field: 'category',
      headerName: t(`entities.product.category`),
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'location',
      headerName: t(`global.locations`),
      filterOperators,
      width: 250,
      renderCell: listCell
    },
    {
      field: 'contract_code',
      headerName: t(`global.contract`),
      width: 130,
      valueGetter: (params) => params.row?.contract_code || '',
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 130,
      valueGetter: (params) => params.row?.supplier || '',
      renderCell: baseCell
    },
    {
      field: 'unit_price',
      headerName: t(`entities.contract_line.unit_price`),
      width: 130,
      valueGetter: (params) => params.row?.unit_price ?? '',
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'initial_available_units',
      headerName: t(`entities.contract_line.initial_available_units`),
      width: 130,
      valueGetter: (params) => params.row?.initial_available_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'available_units',
      headerName: t(`entities.contract_line.available_units`),
      width: 130,
      valueGetter: (params) => params.row?.available_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'initial_discounted_units',
      headerName: t(`entities.contract_line.initial_discounted_units`),
      width: 150,
      valueGetter: (params) => params.row?.initial_discounted_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'available_discounted_units',
      headerName: t(`entities.contract_line.available_discounted_units`),
      width: 150,
      valueGetter: (params) => params.row?.initial_discounted_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      filterOperators,
      width: 350,
      renderCell: baseCell
    },
    {
      field: 'label',
      width: 100,
      valueGetter: (params) => params.row?.label,
      headerName: t(`global.label`)
    }
  ];

  ['restock_threshold', 'understock_threshold', 'overstock_threshold'].forEach((threshold) => {
    columns.push({
      field: threshold,
      headerName: t(`entities.product.${threshold}`),
      type: 'number',
      width: 100,
      filterOperators: numberFilterOperators,
      renderCell: baseCell
    });
  });

  if (isBottomFromTenantAdmin() && disableEdit) {
    columns.push({
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 50,
      renderCell: (params) => {
        return (
          <ActionMenuCell
            row={params.row}
            onManagePrices={onManagePrices}
            t={t}
            refresh={refresh}
          />
        );
      }
    });
  }

  return columns;
}
